import {
  ELandingBeaconStage,
  LANDING_BEACON_ELEMENT_ID,
  LANDING_BEACON_ROUTE,
  markLandingAuth,
} from '@web-store-platforms/utils';

/**
 * The portal's half of the landing reporter.
 *
 * Everything a landing report says is defined once, in
 * packages/utils/src/landingBeacon.ts, so that the store and the portal describe
 * the same journey in the same words and one query can follow a player across
 * both. Only what is genuinely the portal's own lives here.
 */
export {
  ELandingBeaconStage,
  LANDING_BEACON_AUTH_STATES,
  LANDING_BEACON_BOOTED_EVENT,
  LANDING_BEACON_ELEMENT_ID,
  LANDING_BEACON_ERRORS_ATTRIBUTE,
  LANDING_BEACON_FIRST_REPORT,
  LANDING_BEACON_MILESTONES_MS,
  LANDING_BEACON_PARSED_FLAG,
  LANDING_BEACON_RENDERED_EVENT,
  LANDING_BEACON_ROUTE,
  LANDING_BEACON_SRC,
  LANDING_BEACON_STAGES,
  announceLandingBoot,
  announceLandingRender,
  isLandingKey,
  markLandingAuth,
} from '@web-store-platforms/utils';

/**
 * The global the app sets once it is running, read by the beacon so a report can
 * say whether our code ever executed. Without it every silent landing looks the
 * same, and a page that never booted is indistinguishable from one that booted
 * and then failed — which need opposite fixes.
 *
 * Its name is also the reporter's default, for documents that were served before
 * the reporter was told which flag to read.
 */
export const PORTAL_BOOTED_FLAG = '__acPortalBooted';

/**
 * The two globals that say what the booted app has actually done for the player.
 *
 * Booting is announced before the portal decides whether to show anything: on a
 * credential landing every pixel is held back until the login settles, so a
 * player who leaves during that hold left a page that reads as booted and was
 * blank the whole time. The first flag marks the commit that ended the blank
 * page; the second says where the login stood, so a page left with it still
 * `pending` is a login that threw or hung rather than a player who would not
 * wait for one that was working. Both are read by the reporter as they are.
 */
export const PORTAL_RENDERED_FLAG = '__acPortalRendered';
export const PORTAL_AUTH_FLAG = '__acPortalAuth';
export const PORTAL_RECOVERY_EVENT = 'ac-portal-recovery';

export type PortalRecoverySource = 'chunk' | 'route-error' | 'global-error';
export type PortalRecoveryPhase =
  | 'reload-dispatched'
  | 'fallback-displayed'
  | 'retry-selected'
  | 'portal-selected';
export type PortalLandingAuthState = 'pending' | 'ok' | 'failed';

/** What a boundary was handed, reduced to what may be logged about it. */
export interface PortalRecoveryError {
  name?: string;
  message?: string;
  digest?: string;
}

export const markPortalLandingAuth = (state: PortalLandingAuthState): void =>
  markLandingAuth(PORTAL_AUTH_FLAG, state);

export const reportPortalRecovery = (
  source: PortalRecoverySource,
  phase: PortalRecoveryPhase,
  error?: PortalRecoveryError
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const described = describeRecoveryError(error);

    if (document.getElementById(LANDING_BEACON_ELEMENT_ID)) {
      window.dispatchEvent(
        new CustomEvent(PORTAL_RECOVERY_EVENT, {
          detail: { source, phase, ...described },
        })
      );
      return;
    }

    navigator.sendBeacon(
      LANDING_BEACON_ROUTE,
      new Blob(
        [
          JSON.stringify({
            stage: ELandingBeaconStage.RECOVERY,
            recoverySource: source,
            recoveryPhase: phase,
            ...described,
          }),
        ],
        { type: 'text/plain' }
      )
    );
  } catch {
    return;
  }
};

/**
 * A production server error arrives with a generic message and a digest that
 * finds it in the server log, so the digest travels with the message. Both are
 * bounded again by the receiver; this only keeps the beacon body small.
 */
const describeRecoveryError = (
  error: PortalRecoveryError | undefined
): { errorName?: string; errorMessage?: string } => {
  if (!error) {
    return {};
  }

  const message = [error.message, error.digest ? `digest=${error.digest}` : undefined]
    .filter(Boolean)
    .join(' ')
    .slice(0, 120);

  return {
    errorName: error.name ? String(error.name).slice(0, 40) : undefined,
    errorMessage: message || undefined,
  };
};
